import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Dropdown, Form } from "semantic-ui-react";
import i18nstring, { strings } from "../../i18n";
import { ItemDayScheduled } from "../../Shared/Constants/Item";
import { Button } from "../atoms/Button";
import { FormFieldSpanLabel, FormSectionLargeInput } from "../atoms/FormFields";
import SectionHeading from "../molecules/SectionHeading";
import states from "../../constants/States";
import "./OrganismStyles.scss";
import ConfirmationPopup from "../atoms/ConfirmationPopup";

const STATE_OPTIONS = states.map(s => ({ key: s.id, value: s.value, text: s.label }));
const SELECT_ALL_ZIPS_VALUE = "__SELECT_ALL_ZIPS__";

const DAY_KEYS = Object.keys(ItemDayScheduled);

const EMPTY_SCHEDULE = { AM: [], PM: [] };

const buildInitialDays = (schedule, period) => {
  const result = {};
  DAY_KEYS.forEach(day => { result[day] = false; });
  if (schedule?.[period]) {
    schedule[period].forEach(day => { result[day] = true; });
  }
  return result;
};

const buildInitialFormState = (selectedAddress) => {
  const address = selectedAddress?.address || {};
  return {
    name: selectedAddress?.name || "",
    streetAddress1: address.streetAddress1 || "",
    phoneNumber: address.phoneNumber || "",
    city: address.city || "",
    state: address.state || "",
    zip: address.zip || "",
    instructions: address.instructions,
    selectedZips: selectedAddress?.zips || [],
  };
};

export default function LocationForm({
  selectedAddress,
  selectedSchedule,
  availableZips,
  onSave,
  onSaveComplete,
  onCancel,
}) {
  const [showConfirmDeletionModal, setShowConfirmDeletionModal] = useState(false);
  const [zipDropdownOpen, setZipDropdownOpen] = useState(false);
  const [formState, setFormState] = useState(() => buildInitialFormState(selectedAddress));
  const [amDays, setAmDays] = useState(() =>
    buildInitialDays(selectedSchedule?.deliverySchedule || EMPTY_SCHEDULE, "AM")
  );
  const [pmDays, setPmDays] = useState(() =>
    buildInitialDays(selectedSchedule?.deliverySchedule || EMPTY_SCHEDULE, "PM")
  );
  const lastLoadedRef = useRef({
    initialized: false,
    addressId: null,
    scheduleId: null
  });
  const [validations] = useState({
    name: true,
    city: true,
    phoneNumber: true,
    state: true,
    streetAddress1: true,
    zip: true
  });


  useEffect(() => {
    const addressId = selectedAddress?.id || null;
    const scheduleId = selectedSchedule?.id || null;
    if (
      lastLoadedRef.current.initialized &&
      lastLoadedRef.current.addressId === addressId &&
      lastLoadedRef.current.scheduleId === scheduleId
    ) {
      return;
    }
    lastLoadedRef.current = {
      initialized: true,
      addressId,
      scheduleId
    };

    setFormState(buildInitialFormState(selectedAddress));
    const sched = selectedSchedule?.deliverySchedule || EMPTY_SCHEDULE;
    setAmDays(buildInitialDays(sched, "AM"));
    setPmDays(buildInitialDays(sched, "PM"));
  }, [selectedAddress, selectedSchedule, selectedSchedule?.deliverySchedule]);

  const updateField = useCallback((field, value) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  }, []);

  const toggleDay = useCallback((period, day) => {
    const setter = period === "AM" ? setAmDays : setPmDays;
    setter(prev => ({ ...prev, [day]: !prev[day] }));
  }, []);

  const allZipValues = useMemo(() => {
    return Array.from(
      new Set([...(availableZips || []), ...(selectedAddress?.zips || [])])
    );
  }, [availableZips, selectedAddress]);

  const zipOptions = useMemo(() => {
    const options = allZipValues.map(z => ({ key: z, value: z, text: z }));
    if (!allZipValues.length) {
      return options;
    }

    return [
      {
        key: SELECT_ALL_ZIPS_VALUE,
        value: SELECT_ALL_ZIPS_VALUE,
        text: i18nstring(strings.SELECT_ALL_ZIPS)
      },
      ...options
    ];
  }, [allZipValues]);

  const handleZipChange = useCallback((value) => {
    const nextSelectedZips = Array.isArray(value) ? value : [];

    if (nextSelectedZips.includes(SELECT_ALL_ZIPS_VALUE)) {
      updateField("selectedZips", allZipValues);
      setZipDropdownOpen(false);
      return;
    }

    updateField("selectedZips", nextSelectedZips);
  }, [allZipValues, updateField]);

  const handleSave = useCallback(async () => {
    const schedule = {
      AM: DAY_KEYS.filter(d => amDays[d]),
      PM: DAY_KEYS.filter(d => pmDays[d])
    };

    const selectedZips = formState.selectedZips || [];
    const saveSucceeded = await onSave({
      name: formState.name,
      address: {
        streetAddress1: formState.streetAddress1,
        phoneNumber: formState.phoneNumber,
        city: formState.city,
        state: formState.state,
        zip: formState.zip,
        instructions: formState.instructions
      },
      dropoffEnabled: formState.dropoffEnabled,
      deliverySchedule: schedule,
      selectedZips
    });

    if (!saveSucceeded) {
      return;
    }

    if (onSaveComplete) {
      await onSaveComplete();
    } else {
      onCancel();
    }
  }, [amDays, pmDays, formState, onSave, onSaveComplete, onCancel]);  

  return (
    <Form className="loc_form_container loc_form_parent_container">
      <div className="loc_form_section">
        {showConfirmDeletionModal && (
          <ConfirmationPopup
            title={i18nstring(strings.CONFIRMATION)}
            message={i18nstring(strings.CANCEL_LOCATION_CONFIRMATION_MESSAGE)}
            primaryButtonText={i18nstring(strings.CONFIRM)}
            onPrimaryButtonClicked={onCancel}
            open={showConfirmDeletionModal}
            setOpen={setShowConfirmDeletionModal}
          />
        )}
        <div className="loc_form_section_sidebar">
          <SectionHeading title={i18nstring(strings.LOCATION_INFO_HEADING)} />
        </div>
        <div className="loc_form_section_divider" />
        <div className="loc_form_section_content loc_form_location_section_content">
          <FormFieldSpanLabel
            name="name"
            placeholder={i18nstring(strings.LOCATION_NAME)}
            onChange={e => updateField("name", e.target.value)}
            value={formState.name}
            maxChar={50}
            isValid={validations.name}
          />
          <FormFieldSpanLabel
            name="streetAddress1"
            placeholder={i18nstring(strings.STREET_ADDRESS_1)}
            onChange={e => updateField("streetAddress1", e.target.value)}
            value={formState.streetAddress1}
            maxChar={50}
            isValid={validations.streetAddress1}
          />
          <div className="loc_form_city_state_row">
            <div className="loc_form_city_field">
              <FormFieldSpanLabel
                name="city"
                placeholder={i18nstring(strings.CITY)}
                onChange={e => updateField("city", e.target.value)}
                value={formState.city}
                maxChar={30}
                isValid={validations.city}
              />
            </div>
            <div className="loc_form_state_field">
              <div className="modal_form_field loc_form_state_group">
                <label className="modal_form_field_label">
                  {i18nstring(strings.SELECT_STATE)}
                </label>
                <Dropdown
                  className="loc_form_state_dropdown"
                  placeholder={i18nstring(strings.SELECT_STATE)}
                  fluid
                  selection
                  search
                  options={STATE_OPTIONS}
                  value={formState.state}
                  onChange={(e, { value }) => updateField("state", value)}
                />
              </div>
            </div>
            <div className="loc_form_state_field">
              <FormFieldSpanLabel
                name="zip"
                placeholder={i18nstring(strings.ZIP_CODE)}
                onChange={e => updateField("zip", e.target.value)}
                value={formState.zip}
                maxChar={5}
                isValid={validations.zip}
              />
            </div>
          </div>
          <div className="loc_form_phone_field">
            <FormFieldSpanLabel
              name="phoneNumber"
              placeholder={i18nstring(strings.PHONE_NUMBER)}
              onChange={e => updateField("phoneNumber", e.target.value)}
              value={formState.phoneNumber}
              maxChar={10}
              isValid={validations.phoneNumber}
            />
          </div>
        </div>
      </div>

      <div className="loc_form_section">
        <div className="loc_form_section_sidebar">
          <SectionHeading title={i18nstring(strings.DROPOFF_INFO_HEADING)} />
        </div>
        <div className="loc_form_section_divider" />
        <div className="loc_form_section_content">
          <div className="loc_form_field_group loc_form_instructions_group">
            <span className="loc_form_toggle_label">
              {i18nstring(strings.DELIVERY_INSTRUCTIONS)}
            </span>
            <span className="loc_form_toggle_label_desc">
              {i18nstring(strings.DELIVERY_INSTRUCTIONS_NOTE)}
            </span>
            <FormSectionLargeInput
              name="instructions"
              placeholder={i18nstring(strings.DELIVERY_INSTRUCTIONS_GARDENER)}
              onChange={e => updateField("instructions", e.target.value)}
              width={"100%"}
              maxChar={600}
              value={formState.instructions ?? ""}
            />
          </div>

          <div className="loc_form_field_group">
            <label className="loc_form_field_label">
              {i18nstring(strings.LOCATIONS_COL_HOURS_SUMMARY)}
            </label>
            <div className="loc_form_hours_grid">
              <div className="loc_form_hours_row">
                <div className="loc_form_hours_period" />
                {DAY_KEYS.map(day => (
                  <div key={day} className="loc_form_hours_day_header">
                    {i18nstring(strings.DAY_LABELS)[day]}
                  </div>
                ))}
              </div>
              <div className="loc_form_hours_row">
                <div className="loc_form_hours_period">
                  {i18nstring(strings.AM_LABEL)}
                </div>
                {DAY_KEYS.map(day => (
                  <div key={day} className="loc_form_hours_cell">
                    <Checkbox
                      checked={amDays[day]}
                      onChange={() => toggleDay("AM", day)}
                    />
                  </div>
                ))}
              </div>
              <div className="loc_form_hours_row">
                <div className="loc_form_hours_period">
                  {i18nstring(strings.PM_LABEL)}
                </div>
                {DAY_KEYS.map(day => (
                  <div key={day} className="loc_form_hours_cell">
                    <Checkbox
                      checked={pmDays[day]}
                      onChange={() => toggleDay("PM", day)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="loc_form_field_group">
            <label className="loc_form_field_label">
              {i18nstring(strings.ZIP_CODES_SERVED)}
            </label>
            <Dropdown
              placeholder={i18nstring(strings.ZIPS_LABEL)}
              fluid
              multiple
              selection
              search
              open={zipDropdownOpen}
              onOpen={() => setZipDropdownOpen(true)}
              onClose={() => setZipDropdownOpen(false)}
              options={zipOptions}
              value={formState.selectedZips}
              onChange={(e, { value }) => handleZipChange(value)}
            />
          </div>
        </div>
      </div>

      <div className="loc_form_button_row">
        <Button
          className="loc_form_save_button"
          onClick={handleSave}
          disabled={!formState.name}
        >
          {i18nstring(strings.SAVE_CHANGES)}
        </Button>
        <Button
          className="loc_form_cancel_button"
          onClick={() => setShowConfirmDeletionModal(true)}
        >
          {i18nstring(strings.CANCEL)}
        </Button>
      </div>
    </Form>
  );
}
